import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import {
  Circle,
  GoogleMap,
  InfoWindow,
  Marker,
  Polygon,
  Polyline,
  useJsApiLoader,
} from '@react-google-maps/api';
import { GeofenceType, WaypointType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getMapStyles } from '~components/Maps/mapStyles';
import { makeGapOptions } from '~components/Maps/polylineOptions';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '~components/Maps/v2/constants';
import { EditableCircle } from '~components/Maps/v2/EditableCircle';
import { DEFAULT_MAP_CENTER } from '~constants/mapConsts';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { type Point } from '~pages/Admin/Geo/Distances/DistanceMap/catmullRomSpline';
import { splitPingsBySegments } from '~pages/Admin/Geo/Distances/DistanceMap/getCycleSegmentBoundaries';
import { makeTruckMarkerIcon } from '~pages/Admin/Geo/Distances/DistanceMap/timelineTicks';
import {
  createLabeledWaypointSvgDataUrl,
  createWaypointSvgDataUrl,
} from '~pages/Admin/Geo/mapIcons';

import {
  AI_SUMMARY_MAP_COLOR,
  type AiSiteFocus,
  type AiSummaryCircle,
  circleOutlinePath,
} from './aiSummaryCircles';

interface GeofenceShape {
  key: string;
  type: GeofenceType;
  tag: string | null;
  polygonCoordinates: { lat: number; lng: number }[];
  circleCenter: { lat: number; lng: number } | null;
  circleRadius: number | null;
  isSelected: boolean;
  isMoving?: boolean;
  /** Default (candidate) site geofence — rendered muted so it doesn't compete with the marker. */
  isDefaultSite?: boolean;
}

export interface WaypointMarker {
  key: string;
  position: { lat: number; lng: number };
  waypointType: WaypointType | 'both';
  /** When true, renders the amber "?" default-site badge. */
  isDefaultSite?: boolean;
}

interface TruckMarkerData {
  position: Point;
  bearing: number;
}

interface GeofenceDebugMapProps {
  geofences: GeofenceShape[];
  aiSummaryCircles?: AiSummaryCircle[];
  /** Zoom target for an expanded AI site; null fits the view back to everything. */
  focusedAiSite?: AiSiteFocus | null;
  waypoints?: WaypointMarker[];
  pings?: LocationHistoryMinimal[];
  truckMarker?: TruckMarkerData | null;
  hoveredLocation?: { lat: number; lng: number } | null;
  isEditable?: boolean;
  onPolygonEdit?: (coordinates: { lat: number; lng: number }[]) => void;
  onCircleEdit?: (center: { lat: number; lng: number }, radius: number) => void;
  onGeofenceClick?: (key: string) => void;
  sx?: SxProps<Theme>;
}

const HOVERED_LOCATION_ICON_SVG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">` +
      `<circle cx="8" cy="8" r="6" fill="#FF0000" stroke="#FFFFFF" stroke-width="2"/>` +
      `</svg>`,
  );

const ROUTE_POLYLINE_OPTIONS: google.maps.PolylineOptions = {
  strokeColor: '#FF8C00',
  strokeOpacity: 0.8,
  strokeWeight: 3,
};

const ROUTE_GAP_OPTIONS = makeGapOptions('#FF8C00');

const SELECTED_OVERLAY_OPTIONS = {
  fillColor: 'orange',
  fillOpacity: 0.35,
  strokeColor: 'orange',
  strokeOpacity: 0.8,
  strokeWeight: 2,
  suppressUndo: true,
} as google.maps.PolygonOptions;

const INACTIVE_OVERLAY_OPTIONS = {
  fillColor: '#0000FF',
  fillOpacity: 0.25,
  strokeColor: '#0000FF',
  strokeOpacity: 1,
  strokeWeight: 2,
  clickable: true,
  zIndex: 1,
} as google.maps.PolygonOptions;

// Muted variant for default (candidate) site geofences so they recede behind
// the muted default-site marker.
const DEFAULT_SITE_OVERLAY_OPTIONS = {
  ...INACTIVE_OVERLAY_OPTIONS,
  fillOpacity: 0.1,
  strokeOpacity: 0.35,
} as google.maps.PolygonOptions;

// While an AI site is focused, platform geofences recede further so the AI
// circle reads clearly on top of them.
const AI_FOCUSED_INACTIVE_OVERLAY_OPTIONS = {
  ...INACTIVE_OVERLAY_OPTIONS,
  fillOpacity: 0.06,
  strokeOpacity: 0.25,
} as google.maps.PolygonOptions;

const AI_FOCUSED_DEFAULT_SITE_OVERLAY_OPTIONS = {
  ...DEFAULT_SITE_OVERLAY_OPTIONS,
  fillOpacity: 0.04,
  strokeOpacity: 0.15,
} as google.maps.PolygonOptions;

const SELECTED_POLYGON_Z_INDEX = 2;

const TRUCK_ICON_COLOR = '#006F12';

// area wash under the dashed AI ring: strong enough to read as a region even on
// top of a blue platform geofence fill, light enough that the GPS trace and base
// map stay visible through it
const AI_SUMMARY_FILL_OPACITY = 0.12;

// platform geofence overlays use zIndex 1 (inactive) and 2 (selected); the AI
// overlay sits above both so it stays legible where they overlap
const AI_SUMMARY_FILL_Z_INDEX = 2;
const AI_SUMMARY_RING_Z_INDEX = 3;

// dashed ring for the AI circles: fully opaque and heavier than the 0.6-opacity
// scale-3 route dashes from makeGapOptions, which disappeared against overlapping
// geofence fills
const AI_SUMMARY_RING_OPTIONS: google.maps.PolylineOptions = {
  strokeOpacity: 0,
  clickable: true,
  zIndex: AI_SUMMARY_RING_Z_INDEX,
  icons: [
    {
      icon: {
        path: 'M 0,-1 0,1',
        strokeOpacity: 1,
        strokeColor: AI_SUMMARY_MAP_COLOR,
        scale: 4,
      },
      offset: '0',
      repeat: '12px',
    },
  ],
};

/**
 * Lightweight map for Admin > Geo > Jobs > Geofence debug page.
 * All geofences render as native Google Maps overlays.
 * The selected geofence is editable (vertex drag, circle resize) when isEditable is true.
 */
export function GeofenceDebugMap({
  geofences,
  aiSummaryCircles = [],
  focusedAiSite = null,
  waypoints = [],
  pings = [],
  truckMarker,
  hoveredLocation,
  isEditable = false,
  onPolygonEdit,
  onCircleEdit,
  onGeofenceClick,
  sx = {},
}: GeofenceDebugMapProps) {
  const theme = useTheme();
  const mapRef = useRef<google.maps.Map | null>(null);
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const initialFitDone = useRef(false);
  const [activeAiCircle, setActiveAiCircle] = useState<AiSummaryCircle | null>(null);

  // --- Polygon editing (drawableRef pattern from MapV2) ---
  const drawableRef = useRef<google.maps.Polygon | null>(null);

  const onPolygonLoad = useCallback((polygon: google.maps.Polygon) => {
    drawableRef.current = polygon;
  }, []);

  const onPolygonUnmount = useCallback(() => {
    drawableRef.current = null;
  }, []);

  const handlePolygonEdit = useCallback(() => {
    if (!drawableRef.current || !onPolygonEdit) return;
    const nextPath = drawableRef.current
      .getPath()
      .getArray()
      .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
    onPolygonEdit(nextPath);
  }, [onPolygonEdit]);

  // --- Route polyline segments ---
  const { solidSegments, gapSegments } = useMemo(
    () => splitPingsBySegments(pings),
    [pings],
  );

  // --- Selected geofence ---
  const selectedGeofence = useMemo(
    () => geofences.find((gf) => gf.isSelected),
    [geofences],
  );

  // --- Non-selected geofences ---
  const inactiveGeofences = useMemo(
    () => geofences.filter((gf) => !gf.isSelected),
    [geofences],
  );

  // Fit map to all geofences on initial load
  const fitAllGeofences = useCallback(
    (map: google.maps.Map) => {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;

      for (const gf of geofences) {
        if (gf.type === GeofenceType.POLYGON && gf.polygonCoordinates.length >= 3) {
          gf.polygonCoordinates.forEach((c) => {
            bounds.extend(c);
            hasPoints = true;
          });
        } else if (
          gf.type === GeofenceType.CIRCLE &&
          gf.circleCenter &&
          gf.circleRadius
        ) {
          const circle = new window.google.maps.Circle({
            center: gf.circleCenter,
            radius: gf.circleRadius,
          });
          const cb = circle.getBounds();
          if (cb) {
            bounds.union(cb);
            hasPoints = true;
          }
        }
      }

      for (const wp of waypoints) {
        bounds.extend(wp.position);
        hasPoints = true;
      }

      // AI-deduced circles count too — discovered sites can sit far away from
      // any configured geofence and would otherwise start outside the viewport
      for (const aiCircle of aiSummaryCircles) {
        const cb = new window.google.maps.Circle({
          center: aiCircle.center,
          radius: aiCircle.radiusM,
        }).getBounds();
        if (cb) {
          bounds.union(cb);
          hasPoints = true;
        }
      }

      if (hasPoints) {
        map.fitBounds(bounds, 40);
      }
    },
    [geofences, waypoints, aiSummaryCircles],
  );

  // On first load with geofences/waypoints/AI circles, fit to all
  useEffect(() => {
    if (
      mapRef.current &&
      (geofences.length > 0 || waypoints.length > 0 || aiSummaryCircles.length > 0) &&
      !initialFitDone.current
    ) {
      fitAllGeofences(mapRef.current);
      initialFitDone.current = true;
    }
  }, [geofences, aiSummaryCircles, fitAllGeofences]);

  // Zoom to the selected geofence when selection changes, or fit all when deselected.
  const selectedKey = selectedGeofence?.key ?? null;
  const geofencesRef = useRef(geofences);
  geofencesRef.current = geofences;
  const fitAllGeofencesRef = useRef(fitAllGeofences);
  fitAllGeofencesRef.current = fitAllGeofences;
  const prevSelectedKeyRef = useRef<string | null>(null);
  const focusedAiSiteRef = useRef(focusedAiSite);
  focusedAiSiteRef.current = focusedAiSite;
  useEffect(() => {
    if (!mapRef.current) return;

    // Deselected — zoom out to show everything.
    if (!selectedKey) {
      // Skip the initial render where nothing is selected yet.
      if (prevSelectedKeyRef.current === null) return;
      prevSelectedKeyRef.current = null;
      // An expanded AI site owns the viewport; don't zoom back out under it.
      if (focusedAiSiteRef.current) return;
      const map = mapRef.current;
      fitAllGeofencesRef.current(map);
      const container = map.getDiv();
      const observer = new ResizeObserver(() => {
        fitAllGeofencesRef.current(map);
      });
      observer.observe(container);
      const timer = setTimeout(() => observer.disconnect(), 500);
      return () => {
        observer.disconnect();
        clearTimeout(timer);
      };
    }

    prevSelectedKeyRef.current = selectedKey;
    const selected = geofencesRef.current.find((gf) => gf.key === selectedKey);
    if (!selected) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    if (
      selected.type === GeofenceType.POLYGON &&
      selected.polygonCoordinates.length >= 3
    ) {
      selected.polygonCoordinates.forEach((c) => {
        bounds.extend(c);
        hasPoints = true;
      });
    } else if (
      selected.type === GeofenceType.CIRCLE &&
      selected.circleCenter &&
      selected.circleRadius
    ) {
      const circle = new window.google.maps.Circle({
        center: selected.circleCenter,
        radius: selected.circleRadius,
      });
      const cb = circle.getBounds();
      if (cb) {
        bounds.union(cb);
        hasPoints = true;
      }
    }

    if (hasPoints) {
      const map = mapRef.current;
      map.fitBounds(bounds, 40);
      // Re-fit after the map container finishes resizing (e.g. from accordion animation).
      const container = map.getDiv();
      const observer = new ResizeObserver(() => {
        map.fitBounds(bounds, 40);
      });
      observer.observe(container);
      // Stop observing after the accordion animation settles.
      const timer = setTimeout(() => observer.disconnect(), 500);
      return () => {
        observer.disconnect();
        clearTimeout(timer);
      };
    }
  }, [selectedKey]);

  // Zoom to every circle belonging to the focused AI site when it's expanded (a
  // corridor site strings many circles along a road — the fit must cover all of
  // them, not just one), or fit all when collapsed — the same viewport behavior
  // as platform geofence selection above.
  const selectedKeyRef = useRef(selectedKey);
  selectedKeyRef.current = selectedKey;
  const prevFocusedAiSiteRef = useRef<typeof focusedAiSite>(null);
  useEffect(() => {
    if (!mapRef.current) return;

    if (!focusedAiSite) {
      // Skip the initial render where nothing is focused yet.
      if (prevFocusedAiSiteRef.current === null) return;
      prevFocusedAiSiteRef.current = null;
      // A selected platform geofence owns the viewport; don't zoom out under it.
      if (selectedKeyRef.current) return;
      const map = mapRef.current;
      fitAllGeofencesRef.current(map);
      const container = map.getDiv();
      const observer = new ResizeObserver(() => {
        fitAllGeofencesRef.current(map);
      });
      observer.observe(container);
      const timer = setTimeout(() => observer.disconnect(), 500);
      return () => {
        observer.disconnect();
        clearTimeout(timer);
      };
    }

    prevFocusedAiSiteRef.current = focusedAiSite;
    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;
    focusedAiSite.circles.forEach(({ center, radiusM }) => {
      const cb = new window.google.maps.Circle({ center, radius: radiusM }).getBounds();
      if (cb) {
        bounds.union(cb);
        hasPoints = true;
      }
    });
    if (!hasPoints) return;

    const map = mapRef.current;
    map.fitBounds(bounds, 40);
    // Re-fit after the map container finishes resizing (e.g. from accordion animation).
    const container = map.getDiv();
    const observer = new ResizeObserver(() => {
      map.fitBounds(bounds, 40);
    });
    observer.observe(container);
    // Stop observing after the accordion animation settles.
    const timer = setTimeout(() => observer.disconnect(), 500);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [focusedAiSite]);

  // The AI circles load asynchronously after the platform geofences; when they
  // first arrive, re-run the full fit once so discovered sites that sit away
  // from any configured site start inside the viewport.
  const prevAiCircleCountRef = useRef(0);
  useEffect(() => {
    const circlesJustArrived =
      prevAiCircleCountRef.current === 0 && aiSummaryCircles.length > 0;
    prevAiCircleCountRef.current = aiSummaryCircles.length;
    if (!circlesJustArrived || !mapRef.current || !initialFitDone.current) return;
    // an active selection owns the viewport
    if (selectedKeyRef.current || focusedAiSiteRef.current) return;
    fitAllGeofencesRef.current(mapRef.current);
  }, [aiSummaryCircles]);

  /**
   * One center marker per deduced location. The circles are unique per
   * side+location, so a location serving as both pickup and dropoff would
   * otherwise stack two markers — collapse those into one 'both' glyph.
   */
  const aiCircleMarkers = useMemo(() => {
    const markersByLocation = new Map<
      string,
      { key: string; circle: AiSummaryCircle; sides: ('dropoff' | 'pickup')[] }
    >();
    aiSummaryCircles.forEach((aiCircle) => {
      const key = `${aiCircle.center.lat}:${aiCircle.center.lng}:${aiCircle.radiusM}`;
      const existing = markersByLocation.get(key);
      if (existing) {
        if (!existing.sides.includes(aiCircle.side)) {
          existing.sides.push(aiCircle.side);
        }
      } else {
        markersByLocation.set(key, { key, circle: aiCircle, sides: [aiCircle.side] });
      }
    });
    return Array.from(markersByLocation.values()).map(({ key, circle, sides }) => ({
      key,
      circle,
      waypointType:
        sides.length > 1
          ? ('both' as const)
          : sides[0] === 'pickup'
            ? WaypointType.PICKUP
            : WaypointType.DROP_OFF,
    }));
  }, [aiSummaryCircles]);

  if (!isLoaded) return null;

  return (
    <Box sx={{ width: '100%', height: '100%', ...sx }}>
      <GoogleMap
        mapContainerStyle={{ height: '100%', width: '100%' }}
        center={DEFAULT_MAP_CENTER}
        zoom={12}
        onLoad={(map) => {
          mapRef.current = map;

          if (
            geofences.length > 0 ||
            waypoints.length > 0 ||
            aiSummaryCircles.length > 0
          ) {
            fitAllGeofences(map);
            initialFitDone.current = true;
          }
        }}
        onUnmount={() => {
          mapRef.current = null;
        }}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: true,
          gestureHandling: 'greedy',
          styles: getMapStyles(theme.palette.mode === 'dark'),
        }}
      >
        {/* AI-deduced trip geofences — translucent purple wash + dashed outline +
            a center waypoint marker in the AI color. Google Maps Circle overlays
            cannot render dashed strokes, so the outline is drawn as a polyline
            ring using the shared dashed gap options. */}
        {aiSummaryCircles.map((aiCircle) => (
          <Circle
            key={`ai-summary-fill-${aiCircle.key}`}
            center={aiCircle.center}
            radius={aiCircle.radiusM}
            options={{
              fillColor: AI_SUMMARY_MAP_COLOR,
              fillOpacity: AI_SUMMARY_FILL_OPACITY,
              strokeOpacity: 0,
              clickable: false,
              zIndex: AI_SUMMARY_FILL_Z_INDEX,
            }}
          />
        ))}
        {aiSummaryCircles.map((aiCircle) => (
          <Polyline
            key={`ai-summary-circle-${aiCircle.key}`}
            path={circleOutlinePath(aiCircle.center, aiCircle.radiusM)}
            options={AI_SUMMARY_RING_OPTIONS}
            onClick={() => setActiveAiCircle(aiCircle)}
          />
        ))}
        {aiCircleMarkers.map(({ key, circle, waypointType }) => (
          <Marker
            key={`ai-summary-marker-${key}`}
            position={circle.center}
            icon={{
              url: createLabeledWaypointSvgDataUrl(waypointType, AI_SUMMARY_MAP_COLOR),
              anchor: new window.google.maps.Point(12, 12),
            }}
            onClick={() => setActiveAiCircle(circle)}
          />
        ))}
        {activeAiCircle &&
          aiSummaryCircles.some((aiCircle) => aiCircle.key === activeAiCircle.key) && (
            <InfoWindow
              position={activeAiCircle.center}
              onCloseClick={() => setActiveAiCircle(null)}
            >
              <Box sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {t('admin.job_geo_debug.ai_summary_circle_tooltip', {
                  loads: activeAiCircle.visits
                    .map((visit) => visit.loadIndex + 1)
                    .join(', '),
                  name: activeAiCircle.name,
                  side: activeAiCircle.side,
                  source: activeAiCircle.isConfiguredSite
                    ? t('admin.job_geo_debug.ai_summary_circle_configured')
                    : t('admin.job_geo_debug.ai_summary_circle_discovered'),
                })}
              </Box>
            </InfoWindow>
          )}

        {/* Inactive geofences — native overlays, read-only */}
        {inactiveGeofences.map((gf) => {
          const overlayOptions = focusedAiSite
            ? gf.isDefaultSite
              ? AI_FOCUSED_DEFAULT_SITE_OVERLAY_OPTIONS
              : AI_FOCUSED_INACTIVE_OVERLAY_OPTIONS
            : gf.isDefaultSite
              ? DEFAULT_SITE_OVERLAY_OPTIONS
              : INACTIVE_OVERLAY_OPTIONS;
          if (gf.type === GeofenceType.POLYGON && gf.polygonCoordinates.length >= 3) {
            return (
              <Polygon
                key={`inactive-polygon-${gf.key}`}
                paths={gf.polygonCoordinates}
                options={overlayOptions}
                onClick={() => onGeofenceClick?.(gf.key)}
              />
            );
          }
          if (
            gf.type === GeofenceType.CIRCLE &&
            gf.circleCenter &&
            gf.circleRadius &&
            gf.circleRadius > 0
          ) {
            return (
              <Circle
                key={`inactive-circle-${gf.key}`}
                center={gf.circleCenter}
                radius={gf.circleRadius}
                options={overlayOptions as google.maps.CircleOptions}
                onClick={() => onGeofenceClick?.(gf.key)}
              />
            );
          }
          return null;
        })}

        {/* Selected polygon — native overlay for editing support */}
        {selectedGeofence?.type === GeofenceType.POLYGON &&
          selectedGeofence.polygonCoordinates.length >= 3 && (
            <Polygon
              key={`editable-polygon-${selectedGeofence.key}`}
              paths={selectedGeofence.polygonCoordinates}
              editable={isEditable && !selectedGeofence.isMoving}
              draggable={isEditable && !selectedGeofence.isMoving}
              onLoad={onPolygonLoad}
              onUnmount={onPolygonUnmount}
              onMouseUp={handlePolygonEdit}
              onDragEnd={handlePolygonEdit}
              options={{ ...SELECTED_OVERLAY_OPTIONS, zIndex: SELECTED_POLYGON_Z_INDEX }}
            />
          )}

        {/* Selected circle — native overlay for editing support */}
        {selectedGeofence?.type === GeofenceType.CIRCLE &&
          selectedGeofence.circleCenter &&
          selectedGeofence.circleRadius &&
          selectedGeofence.circleRadius > 0 &&
          (isEditable && onCircleEdit && !selectedGeofence.isMoving ? (
            <EditableCircle
              key={`editable-circle-${selectedGeofence.key}`}
              center={selectedGeofence.circleCenter}
              radius={selectedGeofence.circleRadius}
              onEdit={onCircleEdit}
            />
          ) : (
            <Circle
              key={`selected-circle-${selectedGeofence.key}`}
              center={selectedGeofence.circleCenter}
              radius={selectedGeofence.circleRadius}
              options={
                {
                  ...SELECTED_OVERLAY_OPTIONS,
                  zIndex: SELECTED_POLYGON_Z_INDEX,
                } as google.maps.CircleOptions
              }
            />
          ))}

        {/* Route polylines */}
        {solidSegments.map((segment, i) =>
          segment.length > 1 ? (
            <Polyline key={`seg-${i}`} path={segment} options={ROUTE_POLYLINE_OPTIONS} />
          ) : null,
        )}
        {gapSegments.map((segment, i) =>
          segment.length > 1 ? (
            <Polyline key={`gap-${i}`} path={segment} options={ROUTE_GAP_OPTIONS} />
          ) : null,
        )}

        {/* Waypoint markers (read-only) */}
        {waypoints.map((wp) => (
          <Marker
            key={wp.key}
            position={wp.position}
            icon={{
              url: createWaypointSvgDataUrl(wp.waypointType, wp.isDefaultSite),
              anchor: new window.google.maps.Point(12, 12),
            }}
          />
        ))}

        {/* Truck position marker (timeline scrubber) */}
        {truckMarker && (
          <Marker
            position={truckMarker.position}
            icon={makeTruckMarkerIcon(TRUCK_ICON_COLOR, truckMarker.bearing)}
          />
        )}

        {/* Hovered location dot (e.g. from location histories row hover) */}
        {hoveredLocation && (
          <Marker
            position={hoveredLocation}
            icon={{
              url: HOVERED_LOCATION_ICON_SVG,
              anchor: new window.google.maps.Point(8, 8),
            }}
            zIndex={9999}
          />
        )}
      </GoogleMap>
    </Box>
  );
}
